/**
 * Network helpers for the auth screens.
 *
 * Why this exists: when nginx/Cloudflare time out or serve an error page,
 * `fetch` either throws (no CORS headers on the error page) or returns
 * HTML. The login form used to render the raw browser message
 * ("Failed to fetch"), which tells the customer nothing and tells support
 * even less. These helpers retry once on a transient network failure and
 * translate what actually happened into plain language.
 */

const TRANSIENT_STATUSES = [429, 500, 502, 503, 504, 520, 521, 522, 523, 524];

/** Human-readable explanation for a thrown fetch error. */
export function describeFetchError(error) {
  const raw = (error && error.message) || '';
  if (/aborted|timeout/i.test(raw)) {
    return 'The server did not respond in time. Please check your connection and try again.';
  }
  // Chrome: "Failed to fetch", Firefox: "NetworkError when attempting…",
  // Safari: "Load failed". All mean the same: no usable HTTP response.
  if (/failed to fetch|networkerror|load failed|connection/i.test(raw)) {
    return 'Could not reach the server — the request was blocked or timed out before it arrived. '
      + 'If you use a VPN or a corporate network, try turning it off and repeat. '
      + 'If it keeps happening, contact support@auto-remap.com.';
  }
  return `${raw || 'Connection error'} — please try again or contact support.`;
}

/** Message for an HTTP response whose body is not the JSON we expect. */
export function describeGatewayError(status) {
  if ([502, 503, 504, 520, 521, 522, 523, 524].includes(status)) {
    return `The server is temporarily unavailable (HTTP ${status}). Please try again in a minute — the team has been notified.`;
  }
  return `Unexpected server response (HTTP ${status}). Please try again or contact support.`;
}

/**
 * POST once, and retry a single time after a short pause when the attempt
 * fails at the network level or comes back with a transient gateway code.
 * Returns `{ response, raw, data }`; throws only if both attempts threw.
 */
export async function postWithRetry(url, init = {}, { retries = 1, delayMs = 1200 } = {}) {
  let lastError;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      const response = await fetch(url, { method: 'POST', ...init });
      const raw = await response.text();
      let data = {};
      try { data = raw ? JSON.parse(raw) : {}; } catch (_) { data = null; }

      const transient = TRANSIENT_STATUSES.includes(response.status) && response.status !== 429;
      if (transient && attempt < retries) {
        await new Promise((r) => setTimeout(r, delayMs));
        continue;
      }
      return { response, raw, data };
    } catch (error) {
      lastError = error;
      if (attempt < retries) {
        await new Promise((r) => setTimeout(r, delayMs));
        continue;
      }
    }
  }
  throw lastError;
}
